import React, { useContext, useEffect, useRef } from 'react';
import { Modal, StyleSheet, View } from 'react-native';

import { States } from '../types/props';
import DyteLeaveConfirmationDialog from './DyteLeaveMeeting';
import DyteSettingsDialog from './DyteSettings';
import { defaultConfig } from '../lib/default-ui-config';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { UIConfig } from '../types/ui-config';
import DyteClient from '@dytesdk/web-core';
import DyteWebinarConfirmationDialog from './DyteWebinarConfirmationDialog';
import DyteJoinStage from './DyteJoinStage';
import DyteLiveStreamConfirmationDialog from './DyteLiveStreamConfirmationDialog';
import DytePermissionsMessage from './DytePermissionsMessage';
import { extendedMerge } from '../utils/config';
import isEqual from 'lodash/isEqual';
import mergeStates from '../utils/states';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  states,
  // ToDo: Figure out if we need this
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config = defaultConfig,
  iconPack = defaultIconPack,
  // ToDo: Figure out if we need this
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  size,
  t,
  onDyteStateUpdate = () => {},
}: {
  config?: UIConfig;
  iconPack?: IconPack;
  meeting: DyteClient;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  states?: States;
  t?: DyteI18n;
  onDyteStateUpdate?: (e) => void;
}) {
  const prevStatesRef = useRef<States>(states);
  const defaultLang = useLanguage();
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  useEffect(() => {
    if (states) {
      setStates({
        ...extendedMerge(states, storeStates),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const openJoinStageDialog = () => {
      if (
        meeting.stage.status === 'ACCEPTED_TO_JOIN_STAGE' &&
        !storeStates.activeJoinStageConfirmation
      ) {
        setStates((prevStates) => ({
          ...prevStates,
          activeJoinStageConfirmation: true,
        }));
      }
    };

    openJoinStageDialog();
    meeting.stage.addListener('stageStatusUpdate', openJoinStageDialog);
    return () => {
      meeting.stage.removeListener('stageStatusUpdate', openJoinStageDialog);
    };
  }, [meeting, setStates, storeStates.activeJoinStageConfirmation]);
  states = states ? extendedMerge(states, storeStates) : storeStates;
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const visible =
    states.activeLeaveConfirmation ||
    states.activeSettings ||
    states.activeStageConfirmation ||
    states.activeJoinStageConfirmation ||
    !states.permissionGranted;
  const closeDialog = () => {
    if (states.activeLeaveConfirmation) {
      onDyteStateUpdate({ ...states, activeLeaveConfirmation: false });
      setStates({ ...states, activeLeaveConfirmation: false });
    } else if (states.activeSettings) {
      onDyteStateUpdate({ ...states, activeSettings: false });
      setStates({ ...states, activeSettings: false });
    }
  };

  useEffect(() => {
    if (!isEqual(prevStatesRef.current, states)) {
      onDyteStateUpdate(states);
      prevStatesRef.current = states;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states]);

  useEffect(() => {
    const updatedState = mergeStates(states, storeStates);
    onDyteStateUpdate(updatedState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeStates]);

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.4)',
    },
    popUp: {
      width: '95%',
      marginHorizontal: 16,
      paddingBottom: 16,
      borderRadius: 8,
      backgroundColor: colors.background[900],
    },
  });
  return (
    <Modal transparent visible={!!visible} onRequestClose={closeDialog}>
      <View style={styles.container}>
        <View style={styles.popUp}>
          {!states.permissionGranted && (
            <DytePermissionsMessage
              meeting={meeting}
              iconPack={iconPack}
              t={t}
            />
          )}
          {states.activeSettings && (
            <DyteSettingsDialog
              onClose={closeDialog}
              meeting={meeting}
              iconPack={iconPack}
              t={t}
            />
          )}
          {states.activeLeaveConfirmation && (
            <DyteLeaveConfirmationDialog
              onClose={closeDialog}
              meeting={meeting}
              iconPack={iconPack}
              t={t}
            />
          )}
          {states.activeStageConfirmation &&
            meeting.meta.viewType === 'WEBINAR' && (
              <DyteWebinarConfirmationDialog
                meeting={meeting}
                iconPack={iconPack}
                t={t}
              />
            )}
          {states.activeStageConfirmation &&
            meeting.meta.viewType === 'LIVESTREAM' && (
              <DyteLiveStreamConfirmationDialog
                meeting={meeting}
                iconPack={iconPack}
                t={t}
              />
            )}
          {states.activeJoinStageConfirmation && (
            <DyteJoinStage meeting={meeting} iconPack={iconPack} t={t} />
          )}
        </View>
      </View>
    </Modal>
  );
}
